import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import AppState from "../../store/AppState";
import CustomDialog from "../common/DialogContainer";
import { setModalState } from "../../store/modal/ModalActions";
import { selectIsModalOpen } from "../../store/modal/ModalSelectors";
import { loadProductAnalytics } from "../../store/product/ProductActions";
import { selectAllProductsFetched } from "../../store/product/ProductSelectors";
import {
  selectHeatMapResolvedDateRange,
  selectHeatMapBatchPages,
  selectHeatMapMetric,
  selectHeatMapDateRange,
} from "../../store/heatmap/HeatMapSelectors";
import { resetHeatMapState } from "../../store/heatmap/HeatMapActions";
import { HEAT_MAP_DATE_RANGE_OPTIONS } from "../../utils/HeatMapUtils";

import HeatMapConfigPanel from "./HeatMapConfigPanel";
import HeatMapPlanogram from "./HeatMapPlanogram";
import { isHeatMapMetricDateReliant } from "./HeatMapMetricSelect";
import { StyledModalBody, StyledModalTitle } from "./HeatMapModalStyles";

export const heatMapModalId = "heatMapModal";

/**
 * Data Visualization (Heat Map) modal.
 *
 * Two panes: Configuration (left) and Planogram (right).
 *
 * Scope: up to 240 products from the catalog page-batch the user is on
 * when the modal opens. Pages 1–10 → batch 0, 11–20 → batch 1, etc.
 *
 * Analytics fetch behavior:
 *   - Only fired when the selected metric is date-reliant (analytics-only).
 *   - Skipped when the user has selected Specific Date / Date Range but
 *     hasn't yet entered the date(s).
 *   - Fires **one parallel request per P# page** (≤24 productCodes per
 *     call, up to 10 calls per batch). The reducer's `pendingRequests`
 *     counter keeps the skeleton shown until ALL chunks settle.
 *   - Per-payload deduplication via a `Set` ref so re-renders don't
 *     re-dispatch chunks that have already been issued for the current
 *     date range. The Set is cleared whenever the resolved date range
 *     changes, so changing the date range fetches all 10 chunks fresh.
 */
const HeatMapModal: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const open = useSelector((state: AppState) =>
    selectIsModalOpen(state, heatMapModalId),
  );

  const metric = useSelector(selectHeatMapMetric);
  const batchPages = useSelector(selectHeatMapBatchPages);
  const productsById = useSelector(selectAllProductsFetched);
  const dateRange = useSelector(selectHeatMapDateRange);
  const resolvedDateRange = useSelector(selectHeatMapResolvedDateRange);

  // Stable cache key for the batch's product set (across all pages).
  const batchAnalyticsIdsKey = batchPages.pages
    .map((p) => p.productIds.join(","))
    .join("|");

  // Tracks payload keys we've already dispatched. Cleared when the
  // resolved date range changes so a new range fetches all chunks fresh.
  const dispatchedKeysRef = useRef<Set<string>>(new Set());
  const lastDateKeyRef = useRef<string>("");

  useEffect(() => {
    if (!open) return;
    if (batchPages.pages.length === 0) return;

    // Non-date-reliant metrics (Inventory, Price, Stock Value, SKU Coverage)
    // come from product catalog data already in Redux — no fetch needed.
    if (!isHeatMapMetricDateReliant(metric)) return;

    // Wait until the user has filled in the dates for custom modes.
    const isSpecificDateIncomplete =
      dateRange.option === HEAT_MAP_DATE_RANGE_OPTIONS.SPECIFIC_DATE &&
      !dateRange.customStartDate;
    const isCustomRangeIncomplete =
      dateRange.option === HEAT_MAP_DATE_RANGE_OPTIONS.DATE_RANGE &&
      (!dateRange.customStartDate || !dateRange.customEndDate);
    if (isSpecificDateIncomplete || isCustomRangeIncomplete) return;

    // If the resolved date range changed, reset the per-payload Set so all
    // chunks dispatch fresh for the new range.
    const dateKey = `${resolvedDateRange.startDate}|${resolvedDateRange.endDate}`;
    if (dateKey !== lastDateKeyRef.current) {
      lastDateKeyRef.current = dateKey;
      dispatchedKeysRef.current = new Set();
    }

    // Dispatch one chunk per P# page (up to 10 parallel requests).
    batchPages.pages.forEach((page) => {
      const analyticsIds = page.productIds
        .map((id) => productsById[id]?.analyticsId)
        .filter((id): id is string => Boolean(id));
      if (analyticsIds.length === 0) return;

      const payload = {
        startDate: resolvedDateRange.startDate,
        endDate: resolvedDateRange.endDate,
        productCodes: [...analyticsIds].sort(),
      };
      const key = JSON.stringify(payload);
      if (!dispatchedKeysRef.current.has(key)) {
        dispatchedKeysRef.current.add(key);
        dispatch(loadProductAnalytics(payload));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    metric,
    batchAnalyticsIdsKey,
    dateRange.option,
    dateRange.customStartDate,
    dateRange.customEndDate,
    resolvedDateRange.startDate,
    resolvedDateRange.endDate,
    dispatch,
  ]);

  const handleClose = () => {
    dispatch(setModalState(heatMapModalId, false));
    // Reset heat map config (metric, date range, colors) so the next open
    // starts with defaults instead of the previously-chosen values.
    dispatch(resetHeatMapState());
    // Clear the dispatched-keys tracker + last date ref so a fresh open
    // re-evaluates from scratch.
    dispatchedKeysRef.current = new Set();
    lastDateKeyRef.current = "";
  };

  if (!open) {
    return null;
  }

  const titleText = intl.formatMessage(
    {
      id: "heatMap.modalTitle",
      defaultMessage: "Data Visualization - {count} products",
    },
    { count: batchPages.totalProductCount },
  );

  const title = <StyledModalTitle>{titleText}</StyledModalTitle>;

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={title}
      maxWidth="lg"
      fullWidth
    >
      <StyledModalBody>
        <HeatMapConfigPanel />
        <HeatMapPlanogram />
      </StyledModalBody>
    </CustomDialog>
  );
};

export default HeatMapModal;
